import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { buildRegisterProps, callAll, fieldHasErrors, useCombinedRefs } from '../../../../helpers';
import { useLatestRef } from '../../../../helpers/hooks/useLatestRef';
import { inputDefaultProps } from '../../../../props/inputProps';
import { getInputModeProps } from '../../../../helpers/getInputModeProps';

const useFormGroupValidation = props => {
	const {
		dirty,
		validators,
		helptext,
		tooltip,
		groupClassName = inputDefaultProps.groupClassName,
		id,
		label,
		'aria-label': ariaLabel,
		errorLabel,
		inline,
		ref,
		...inputProps
	} = props;
	const isRequired = inputProps.disabled ? false : inputProps.required;
	const formContext = useFormContext() || {};

	const latest = useLatestRef({
		dirty,
		validators,
		helptext,
		tooltip,
		groupClassName,
		id,
		label,
		ariaLabel,
		errorLabel,
		inline,
		ref,
		inputProps,
		isRequired,
		helpblockId: helptext ? id + '_helptext' : null,
		formContext,
		registerProps: buildRegisterProps(
			formContext.register,
			{
				name: inputProps.name,
				required: isRequired,
				validators
			},
			inputProps
		)
	});
	const inputRef = useCombinedRefs(latest.current.registerProps.ref, ref);
	const [isInvalid, setIsInvalid] = useState(dirty && latest.current.isRequired);

	// triggers the invalid state
	const firstValidityUpdate = useRef(dirty);
	useEffect(() => {
		if (firstValidityUpdate.current) {
			firstValidityUpdate.current = false;
			return;
		}

		setIsInvalid(fieldHasErrors(latest.current.inputProps.name, formContext.formState?.errors));
	}, [formContext.formState, latest]);

	const getGroupProps = useCallback(
		(overrideProps = {}) => {
			const { className = latest.current.groupClassName, ...rest } = overrideProps;
			return {
				controlId: latest.current.id,
				className: `${latest.current.inline ? 'row gx-2 ' : ''}${className || ''}`,
				...rest
			};
		},
		[latest]
	);

	const getLabelProps = useCallback(
		(overrideProps = {}) => {
			return {
				id: latest.current.id && latest.current.label ? latest.current.id + '_label' : null,
				children: latest.current.label,
				required: latest.current.inputProps.required,
				tooltip: latest.current.tooltip,
				column: latest.current.inline,
				htmlFor: latest.current.id,
				...overrideProps
			};
		},
		[latest]
	);

	const getInputProps = useCallback(
		(overrideProps = {}) => {
			const { onChange, onBlur, ...mergedProps } = { ...latest.current.inputProps, ...overrideProps };
			return {
				id: latest.current.id,
				'aria-required': latest.current.inputProps.required,
				'aria-invalid': isInvalid,
				'aria-label': latest.current.label ? null : latest.current.ariaLabel || null,
				'aria-describedby': latest.current.helpblockId,
				onChange: callAll(onChange, latest.current.registerProps.onChange),
				onBlur: callAll(onBlur, latest.current.registerProps.onBlur),
				...mergedProps,
				...getInputModeProps(latest.current.inputProps.type, latest.current.inputProps.inputMode)
			};
		},
		[isInvalid, latest]
	);

	const getHelpblockProps = useCallback(
		(overrideProps = {}) => {
			return {
				id: latest.current.helpblockId,
				children: latest.current.helptext,
				...overrideProps
			};
		},
		[latest]
	);

	const getValidation = useCallback(() => {
		return {
			isRequired: latest.current.isRequired,
			isInvalid,
			setIsInvalid,
			formContext: latest.current.formContext,
			validators,
			inputName: latest.current.inputProps.name,
			displayLabel: latest.current.label || latest.current.ariaLabel || latest.current.errorLabel || ''
		};
	}, [isInvalid, latest, validators]);

	return {
		getGroupProps,
		getLabelProps,
		getInputProps,
		getHelpblockProps,
		inputRef,
		validation: getValidation()
	};
};

export default useFormGroupValidation;
